import React from 'react'
import Box from '@mui/material/Box'

import { Address } from '@ecm/data/Address'
import Map from '@ecm/ui/element/Map'
import { useMapReferenceContext } from '@ecm/ui/provider/MapReferenceProvider'
import { useVisibleAddressesContext } from '@ecm/ui/provider/VisibleAddressesProvider'
import { updateMapBounds } from '@ecm/utils/map'

type AddressMapProps = {
  addresses: Address[]
  height?: string
}

export const AddressMap = ({ addresses }: AddressMapProps) => {
  const {
    visibleAddresses,
    isFirstZoomAfterSearch,
    setVisibleAddresses,
    setSearchValue,
    setIsFirstZoomAfterSearch,
  } = useVisibleAddressesContext()

  const { DEFAULT_MAX_ZOOM, setMapReference, center, setCenter, mapReference, isLoaded } =
    useMapReferenceContext()

  const onMapLoad = (map: google.maps.Map) => {
    setMapReference(map)
    updateMapBounds(visibleAddresses, map)

    map.setOptions({ maxZoom: DEFAULT_MAX_ZOOM })
  }

  const updateVisibleAddresses = (addresses: Address[]) => {
    if (!mapReference) {
      return
    }

    const newBounds = mapReference.getBounds()

    if (newBounds) {
      const addressesInBounds = addresses.filter((a: Address) => newBounds.contains(a.location))

      setVisibleAddresses(addressesInBounds)
    }
  }

  const onDragEnd = () => {
    updateVisibleAddresses(addresses)

    if (mapReference) {
      setCenter(mapReference.getCenter())
    }

    setSearchValue('')
  }

  const onZoomChanged = () => {
    updateVisibleAddresses(isFirstZoomAfterSearch ? visibleAddresses : addresses)

    if (isFirstZoomAfterSearch) {
      setIsFirstZoomAfterSearch(false)
    } else {
      setSearchValue('')
    }
  }

  const onClickMarker = (address: Address) => () => {
    setCenter(address.location)

    if (mapReference) {
      const currentZoom = mapReference.getZoom() ?? 0
      mapReference.setZoom(currentZoom + 3)
    }

    updateVisibleAddresses(addresses)
  }

  return (
    <Box height="600px">
      {isLoaded && (
        <Map
          center={center}
          markers={visibleAddresses}
          onClickMarker={onClickMarker}
          onDblClickMarker={onClickMarker}
          onDragEnd={onDragEnd}
          onLoad={onMapLoad}
          onZoomChanged={onZoomChanged}
        />
      )}
    </Box>
  )
}

export default AddressMap
